<template>
  <div class="inbox-wrap">
    <b-row v-if="!effectiveActive">
      <b-col xs="12" md="6" offset-md="3">
        <b-card class="text-center py-4">
          <b-icon icon="envelope-open" font-scale="3" class="text-primary mb-3" />
          <h4>{{ effectiveModel && $translateLabel(effectiveModel.label) }}</h4>
          <p class="text-muted">Connect your email accounts (Gmail, Outlook, etc.) to send and receive emails directly from your workflows. Supports IMAP/SMTP.</p>
          <StartTrialFooter :integration="effectiveModel" />
        </b-card>
      </b-col>
    </b-row>

    <div v-else class="gm-root">
      <InboxSidebar
        :folders="folders"
        :current-folder="inboxFolder"
        :unread-count="unreadCount"
        :has-draft="hasDraft"
        :show-compose="showCompose"
        :imap-folders="imapFolders"
        :accounts="accounts"
        :selected-account="selectedAccount"
        :can-send="canSend"
        @compose="openCompose()"
        @folder-change="switchFolder"
        @create-folder="openCreateFolderModal"
        @account-select="selectAccount"
        @account-options="openAccountOptions"
        @add-account="openAddAccountModal"
        @manage-accounts="showAccounts = true"
      />

      <div class="gm-main">
        <InboxEmailList
          v-show="!selectedEmail"
          :emails="pagedEmails"
          :threads="threads"
          :selected-email-ids="selectedEmails"
          :active-email-id="selectedEmail ? selectedEmail.id : null"
          :loading="loadingEmails"
          :has-account="!!selectedAccount"
          :current-folder="inboxFolder"
          :current-page="emailPage"
          :total-pages="totalPages"
          :total-emails="totalEmails"
          :page-size="pageSize"
          :emails-error="emailsError"
          :account-error="accountError"
          :all-selected="allSelected"
          :some-selected="someSelected"
          :move-target-folders="moveTargetFolders"
          :imap-folders="imapFolders"
          :can-send="canSend"
          :can-delete="canDelete"
          @toggle-select-all="toggleSelectAll"
          @toggle-select="toggleSelectEmail"
          @bulk-mark-read="bulkMarkRead"
          @bulk-delete="bulkDelete"
          @bulk-star="bulkStar"
          @bulk-move-to="bulkMoveTo"
          @bulk-move-to-spam="bulkMoveToSpam"
          @deselect-all="selectedEmails = new Set()"
          @page-size-change="onPageSizeChange"
          @prev-page="prevPage"
          @next-page="nextPage"
          @refresh="loadEmails"
          @dismiss-error="dismissError"
          @email-click="openEmail"
          @toggle-star="toggleStar"
          @quick-reply="replyTo"
          @quick-forward="forwardEmail"
          @quick-toggle-read="toggleRead"
          @quick-delete="deleteEmail"
          @add-account="openAddAccountModal"
        />

        <transition name="gm-detail-slide">
          <InboxEmailDetail
            v-if="selectedEmail"
            :email="selectedEmail"
            :thread-messages="threadMessages"
            :loading-thread="loadingThread"
            :current-folder="inboxFolder"
            :selected-account="selectedAccount"
            :inline-sending="inlineSending"
            :can-send="canSend"
            :can-delete="canDelete"
            @close="closeDetail"
            @forward="forwardEmail(selectedEmail)"
            @toggle-read="toggleRead(selectedEmail)"
            @move-to="moveEmailTo(selectedEmail, $event)"
            @delete="deleteEmail(selectedEmail)"
            @toggle-star="toggleStar(selectedEmail)"
            @download-attachment="downloadAttachment"
            @inline-reply-send="handleInlineReplySend"
            @reply-popout="handleReplyPopout"
          />
        </transition>
      </div>
    </div>

    <div v-if="showCompose && canSend && composeMode === 'dialog'" class="compose-backdrop" @click.self="composeMode = 'minimized'" />
    <InboxComposePanel
        v-show="showCompose && canSend"
        ref="composePanel"
        :class="['compose-panel--' + composeMode]"
        :title="composeTitle"
        :to.sync="emailForm.to"
        :cc.sync="emailForm.cc"
        :subject.sync="emailForm.subject"
        :body-html.sync="emailForm.htmlBody"
        :attachments="composeAttachments"
        :suggestions="filteredContacts"
        :show-suggestions="showToSuggestions"
        :show-cc="showCc"
        :sending="sending"
        :error="sendError"
        :selected-account="selectedAccount"
        :placeholder="composePlaceholder"
        :compose-mode="composeMode"
        @minimize="composeMode = 'minimized'"
        @maximize="composeMode = 'dialog'"
        @discard="discardCompose"
        @dismiss-error="sendError = null"
        @to-focus="onComposeToInput"
        @to-blur="hideToSuggestions"
        @select-contact="selectContact"
        @toggle-cc="showCc = !showCc"
        @exec-cmd="execCmd"
        @insert-link="insertLink"
        @files-selected="onFilesSelected"
        @remove-attachment="composeAttachments.splice($event, 1)"
        @send="sendEmail"
      />

    <InboxModals
      :show-accounts.sync="showAccounts"
      :accounts="accounts"
      :selected-account-id="selectedAccount ? selectedAccount.id : null"
      :loading-accounts="loadingAccounts"
      :folder-name.sync="newFolderName"
      :folder-error="folderError"
      :adding-account="addingAccount"
      @select-account="selectAccount($event); showAccounts = false"
      @test-connection="testConnection"
      @delete-account="deleteAccount"
      @rename-account="renameAccount"
      @manage-policies="openPolicies"
      @add-account="showAccounts = false; openAddAccountModal()"
      @create-folder="submitCreateFolder"
      @dismiss-folder-error="folderError = null"
      @submit-account="submitAddAccount"
    >
      <template #add-account-form>
        <b-alert variant="danger" dismissible :show="!!addAccountError" @dismissed="addAccountError=null">{{ addAccountError }}</b-alert>
        <b-form>
          <b-form-group label="Email Address" label-for="acc-email">
            <b-form-input id="acc-email" v-model="newAccount.email" type="email" placeholder="you@gmail.com" required />
            <b-form-text v-if="detectedProvider" class="text-success"><b-icon icon="check-circle-fill" /> Detected: <strong>{{ detectedProvider.name }}</strong> — settings auto-filled</b-form-text>
            <b-form-text v-else-if="newAccount.email && newAccount.email.includes('@')" class="text-warning"><b-icon icon="exclamation-triangle-fill" /> Unknown provider — select below</b-form-text>
          </b-form-group>
          <b-form-group v-if="newAccount.email && newAccount.email.includes('@') && !detectedProvider" label="Email Provider" label-for="acc-provider">
            <b-form-select id="acc-provider" v-model="manualProvider" :options="providerOptions" />
          </b-form-group>
          <b-form-group label="App Password" label-for="acc-app-pass">
            <b-form-input id="acc-app-pass" v-model="simplePassword" type="password" placeholder="App password" required />
            <b-form-text>
              <span v-if="activeProvider && activeProvider.helpUrl"><a :href="activeProvider.helpUrl" target="_blank">How to get an app password for {{ activeProvider.name }}</a></span>
              <span v-else class="text-muted">Use an app-specific password from your email provider.</span>
            </b-form-text>
          </b-form-group>
          <b-link class="small" @click="showAdvanced = !showAdvanced">{{ showAdvanced ? '▲ Hide advanced' : '▼ Advanced settings' }}</b-link>
          <div v-if="showAdvanced" class="mt-3">
            <b-form-group class="mb-3">
              <b-form-checkbox v-model="skipConnectionTest">Save without testing connection</b-form-checkbox>
            </b-form-group>
            <b-form-group label="Account Name" label-for="acc-name">
              <b-form-input id="acc-name" v-model="newAccount.name" placeholder="My Gmail" />
            </b-form-group>
            <hr /><h6>IMAP (Incoming)</h6>
            <b-row>
              <b-col md="6"><b-form-group label="Host"><b-form-input v-model="newAccount.imap_config.host" placeholder="imap.gmail.com" /></b-form-group></b-col>
              <b-col md="3"><b-form-group label="Port"><b-form-input v-model.number="newAccount.imap_config.port" type="number" /></b-form-group></b-col>
              <b-col md="3"><b-form-group label="TLS"><b-form-checkbox v-model="newAccount.imap_config.use_tls" switch class="mt-2">TLS</b-form-checkbox></b-form-group></b-col>
            </b-row>
            <b-row>
              <b-col md="6"><b-form-group label="Username"><b-form-input v-model="newAccount.imap_config.username" /></b-form-group></b-col>
              <b-col md="6"><b-form-group label="Password"><b-form-input v-model="newAccount.imap_config.password" type="password" /></b-form-group></b-col>
            </b-row>
            <hr /><h6>SMTP (Outgoing)</h6>
            <b-row>
              <b-col md="6"><b-form-group label="Host"><b-form-input v-model="newAccount.smtp_config.host" placeholder="smtp.gmail.com" /></b-form-group></b-col>
              <b-col md="3"><b-form-group label="Port"><b-form-input v-model.number="newAccount.smtp_config.port" type="number" /></b-form-group></b-col>
              <b-col md="3"><b-form-group label="TLS"><b-form-checkbox v-model="newAccount.smtp_config.use_tls" switch class="mt-2">TLS</b-form-checkbox></b-form-group></b-col>
            </b-row>
            <b-row>
              <b-col md="6"><b-form-group label="Username"><b-form-input v-model="newAccount.smtp_config.username" /></b-form-group></b-col>
              <b-col md="6"><b-form-group label="Password"><b-form-input v-model="newAccount.smtp_config.password" type="password" /></b-form-group></b-col>
            </b-row>
          </div>
        </b-form>
      </template>
    </InboxModals>

    <!-- Policies Modal -->
    <main-dialog
      :show="showPolicies"
      :title="$t('inbox.policies')"
      :subtitle="policiesAccount ? policiesAccount.email : ''"
      icon=""
      max-width="600px"
      @close="showPolicies = false"
    >
      <!-- Error alert -->
      <b-alert variant="danger" dismissible :show="!!policyError" @dismissed="policyError=null" class="pol-alert mb-3">{{ policyError }}</b-alert>

      <!-- Loading -->
      <div v-if="loadingPolicies" class="pol-empty">
        <b-spinner variant="primary" />
      </div>

      <!-- Empty state -->
      <div v-else-if="!policies.length && !showPolicyForm" class="pol-empty">
        <div class="pol-empty-icon">
          <b-icon icon="shield-lock" font-scale="2.5" />
        </div>
        <p class="pol-empty-title">{{ $t('inbox.noPolicies') }}</p>
        <p class="pol-empty-desc">{{ $t('inbox.noPoliciesDesc') }}</p>
        <b-button variant="primary" size="sm" class="pol-btn" @click="showPolicyForm = true; editingPolicy = null; policyForm = { type: 'data_access', config: { targets: [], actions: [] } }; policyTargetEverybody = false">
          <b-icon icon="plus" class="mr-1" /> {{ $t('inbox.addPolicy') }}
        </b-button>
      </div>

      <!-- Policy list -->
      <div v-else-if="!showPolicyForm">
        <div v-for="policy in policies" :key="policy.id" class="pol-card">
          <div class="pol-card-header">
            <div class="pol-card-icon">
              <b-icon icon="shield-check" />
            </div>
            <div class="pol-card-info">
              <div class="pol-card-label">{{ $t('inbox.dataAccess') }}</div>
              <div class="pol-card-targets">
                <b-icon icon="people" class="mr-1" font-scale="0.85" />
                <span v-if="policy.config.targets && policy.config.targets.includes('everybody')">{{ $t('inbox.everybody') }}</span>
                <span v-else>{{ resolveTargetNames(policy.config.targets) }}</span>
              </div>
            </div>
            <div class="pol-card-actions-btns">
              <button class="pol-icon-btn" @click="editPolicy(policy)" :title="$t('inbox.editPolicy')">
                <b-icon icon="pencil" />
              </button>
              <button class="pol-icon-btn pol-icon-btn--danger" @click="removePolicy(policy)" :title="$t('inbox.deletePolicy')">
                <b-icon icon="trash" />
              </button>
            </div>
          </div>
          <div class="pol-card-badges">
            <span v-for="a in (policy.config.actions || [])" :key="a" :class="['pol-action-badge', 'pol-action-badge--' + a]">
              <b-icon :icon="a === 'read' ? 'eye' : a === 'send' ? 'send' : 'trash'" font-scale="0.8" class="mr-1" />
              {{ $t('inbox.action' + a.charAt(0).toUpperCase() + a.slice(1)) }}
            </span>
          </div>
        </div>

        <button class="pol-add-btn" @click="showPolicyForm = true; editingPolicy = null; policyForm = { type: 'data_access', config: { targets: [], actions: [] } }; policyTargetEverybody = false">
          <b-icon icon="plus-circle" class="mr-2" /> {{ $t('inbox.addPolicy') }}
        </button>
      </div>

      <!-- Policy Form -->
      <div v-if="showPolicyForm" class="pol-form">
        <div class="pol-form-title">{{ editingPolicy ? $t('inbox.editPolicy') : $t('inbox.addPolicy') }}</div>

        <div class="pol-form-section">
          <label class="pol-form-label">
            <b-icon icon="people" class="mr-1" /> {{ $t('inbox.policyTargets') }}
          </label>
          <div class="pol-everybody-toggle" @click="onEverybodyToggle(!policyTargetEverybody)">
            <div :class="['pol-toggle', { 'pol-toggle--active': policyTargetEverybody }]">
              <div class="pol-toggle-knob" />
            </div>
            <span>{{ $t('inbox.everybody') }}</span>
          </div>
          <v-select
            v-if="!policyTargetEverybody"
            v-model="policyForm.config.targets"
            :options="availableRoles"
            label="name"
            :reduce="role => role.uuid"
            multiple
            :placeholder="$t('inbox.selectRoles')"
            :append-to-body="true"
            class="pol-role-select"
          />
        </div>

        <div class="pol-form-section">
          <label class="pol-form-label">
            <b-icon icon="lightning" class="mr-1" /> {{ $t('inbox.policyActions') }}
          </label>
          <div class="pol-actions-grid">
            <label v-for="action in ['read', 'send', 'delete']" :key="action"
              :class="['pol-action-option', { 'pol-action-option--selected': policyForm.config.actions.includes(action) }]"
              @click="toggleAction(action)"
            >
              <b-icon :icon="action === 'read' ? 'eye' : action === 'send' ? 'cursor' : 'trash'" font-scale="1.1" class="pol-action-option-icon" />
              <span>{{ $t('inbox.action' + action.charAt(0).toUpperCase() + action.slice(1)) }}</span>
              <div v-if="policyForm.config.actions.includes(action)" class="pol-action-check">
                <b-icon icon="check" font-scale="0.8" />
              </div>
            </label>
          </div>
        </div>

        <div class="pol-form-footer">
          <button class="pol-btn pol-btn--ghost" @click="showPolicyForm = false; editingPolicy = null">{{ $t('inbox.cancel') }}</button>
          <button class="pol-btn pol-btn--primary" :disabled="savingPolicy || !policyForm.config.targets.length || !policyForm.config.actions.length" @click="submitPolicy">
            <b-spinner v-if="savingPolicy" small class="mr-1" />
            {{ editingPolicy ? $t('inbox.save') : $t('inbox.addPolicy') }}
          </button>
        </div>
      </div>
    </main-dialog>

  </div>
</template>

<style>
/* ── Hide footer on inbox page ── */
body.inbox-page-active .app-footer {
  display: none !important;
}

/* ── Root layout ── */
.inbox-wrap { 
  position: relative; 
  margin-right: -30px;
  margin-top: -25px;
  margin-left: -30px;
}
.gm-root {
  display: flex;
  height: calc(100vh - 88px);
  min-height: 520px;
  background: var(--bg-card);
  border: 1px solid var(--border-separator);
  border-radius: 8px;
  overflow: hidden;
  font-size: 0.8125rem;
  color: var(--text-primary);
}
.gm-main {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-width: 0;
}

/* ── Detail slide transition ── */
.gm-detail-slide-enter-active { transition: transform 0.22s cubic-bezier(0.4,0,0.2,1), opacity 0.18s; }
.gm-detail-slide-leave-active { transition: none; }
.gm-detail-slide-enter { transform: translateX(32px); opacity: 0; }
.gm-detail-slide-leave-to { display: none; }

/* ── Compose backdrop ── */
.compose-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 1049;
}

/* ── Compose panel: minimised (bottom-right strip) ── */
.compose-panel--minimized {
  position: fixed;
  bottom: 0;
  right: 24px;
  width: 580px;
  max-width: calc(100vw - 220px);
  max-height: 600px;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary, #1e1f26);
  border: 1px solid var(--border-separator, #2a2d35);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -4px 16px rgba(0,0,0,0.3);
  z-index: 1050;
}

/* ── Compose panel: dialog (centered modal) ── */
.compose-panel--dialog {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1020px;
  max-width: calc(100vw - 60px);
  height: 94vh;
  max-height: 94vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary, #1e1f26);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
  z-index: 1050;
}

/* Minimised slide-up transition */
.compose-slide-enter-active,
.compose-slide-leave-active { transition: transform 0.22s cubic-bezier(0.4,0,0.2,1), opacity 0.18s; }
.compose-slide-enter, .compose-slide-leave-to { transform: translateY(100%); opacity: 0; }

/* Dialog fade-scale transition */
.compose-dialog-enter-active,
.compose-dialog-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.compose-dialog-enter, .compose-dialog-leave-to { opacity: 0; transform: translate(-50%, -50%) scale(0.92); }
.compose-dialog-enter-to { opacity: 1; transform: translate(-50%, -50%) scale(1); }

/* Policy styles */
.pol-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;
}
.pol-empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(99, 102, 241, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #6366f1;
}
.pol-empty-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  color: var(--text-primary);
}
.pol-empty-desc {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-bottom: 1.25rem;
  max-width: 280px;
}

/* Policy card */
.pol-card {
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 10px;
  padding: 0.875rem 1rem;
  margin-bottom: 0.625rem;
  transition: border-color 0.15s;
}
.pol-card:hover {
  border-color: var(--border-hover, #3a3d48);
}
.pol-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.pol-card-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.12);
  color: #818cf8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.95rem;
}
.pol-card-info {
  flex: 1;
  min-width: 0;
}
.pol-card-label {
  font-weight: 600;
  font-size: 0.82rem;
  color: var(--text-primary);
  line-height: 1.2;
}
.pol-card-targets {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 2px;
  display: flex;
  align-items: center;
}
.pol-card-actions-btns {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.pol-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  font-size: 0.82rem;
}
.pol-icon-btn:hover {
  background: var(--bg-card-secondary, #22232b);
  color: var(--text-primary);
}
.pol-icon-btn--danger:hover {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

/* Action badges */
.pol-card-badges {
  display: flex;
  gap: 6px;
  margin-top: 0.625rem;
  padding-left: calc(34px + 0.75rem);
}
.pol-action-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}
.pol-action-badge--read {
  background: rgba(59, 130, 246, 0.12);
  color: #60a5fa;
}
.pol-action-badge--send {
  background: rgba(16, 185, 129, 0.12);
  color: #34d399;
}
.pol-action-badge--delete {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
}

/* Add policy button */
.pol-add-btn {
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.625rem;
  border-radius: 10px;
  border: 1px dashed var(--border-separator, #2a2d35);
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.82rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.pol-add-btn:hover {
  border-color: #6366f1;
  color: #818cf8;
  background: rgba(99, 102, 241, 0.06);
}

/* ── Policy Form ── */
.pol-form {
  padding: 0;
}
.pol-form-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.25rem;
  color: var(--text-primary);
}
.pol-form-section {
  margin-bottom: 1.25rem;
}
.pol-form-label {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.625rem;
}

/* Everybody toggle */
.pol-everybody-toggle {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.625rem;
  font-size: 0.82rem;
  color: var(--text-primary);
  transition: background 0.15s;
}
.pol-everybody-toggle:hover {
  background: var(--bg-card-secondary, #22232b);
}
.pol-toggle {
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: var(--border-separator, #3a3d48);
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}
.pol-toggle--active {
  background: #6366f1;
}
.pol-toggle-knob {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}
.pol-toggle--active .pol-toggle-knob {
  transform: translateX(16px);
}

/* Role selector */
.pol-role-select {
  position: relative;
  z-index: 1;
}
.pol-role-select .vs__dropdown-toggle {
  background: var(--bg-primary, #16171d);
  border-color: var(--border-separator, #2a2d35);
  border-radius: 8px;
  min-height: 38px;
  cursor: pointer;
}
.pol-role-select .vs__search,
.pol-role-select .vs__search:focus {
  color: var(--text-primary, #e4e6eb);
  margin: 2px 0;
}
.pol-role-select .vs__search::placeholder {
  color: var(--text-secondary);
}
.pol-role-select .vs__selected {
  color: var(--text-primary, #e4e6eb);
  background: rgba(99, 102, 241, 0.15);
  border-color: rgba(99, 102, 241, 0.3);
  border-radius: 4px;
  font-size: 0.82rem;
}
.pol-role-select .vs__deselect svg {
  fill: var(--text-secondary);
}
.pol-role-select .vs__open-indicator {
  fill: var(--text-secondary);
}
.pol-role-select .vs__actions {
  padding: 4px 8px 0 4px;
}
.pol-role-select .vs__clear svg {
  fill: var(--text-secondary);
}
/* Dropdown appended to body — must be above modal z-index 1060 */
.pol-role-select .vs__dropdown-menu,
body > .vs__dropdown-menu {
  z-index: 9999 !important;
  background: var(--bg-card, #1e1f26);
  border-color: var(--border-separator, #2a2d35);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  padding: 4px;
}
.pol-role-select .vs__dropdown-option,
body > .vs__dropdown-menu .vs__dropdown-option {
  color: var(--text-primary, #e4e6eb);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
}
.pol-role-select .vs__dropdown-option--highlight,
body > .vs__dropdown-menu .vs__dropdown-option--highlight {
  background: rgba(99, 102, 241, 0.15);
  color: #818cf8;
}
.pol-role-select .vs__no-options {
  color: var(--text-secondary);
  font-size: 0.82rem;
  padding: 8px 12px;
}

/* Actions grid */
.pol-actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.pol-action-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0.875rem 0.5rem;
  border-radius: 10px;
  border: 1px solid var(--border-separator, #2a2d35);
  background: var(--bg-primary, #16171d);
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
  font-size: 0.78rem;
  color: var(--text-secondary);
  text-align: center;
  user-select: none;
}
.pol-action-option:hover {
  border-color: var(--border-hover, #3a3d48);
  background: var(--bg-card-secondary, #22232b);
}
.pol-action-option--selected {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.08);
  color: var(--text-primary);
}
.pol-action-option-icon {
  color: var(--text-secondary);
}
.pol-action-option--selected .pol-action-option-icon {
  color: #818cf8;
}
.pol-action-check {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #6366f1;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form footer */
.pol-form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-separator, #2a2d35);
}

/* Buttons */
.pol-btn {
  padding: 0.5rem 1.125rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s;
}
.pol-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pol-btn--primary {
  background: #6366f1;
  color: #fff;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);
}
.pol-btn--primary:hover:not(:disabled) {
  background: #5558e6;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}
.pol-btn--ghost {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-separator, #2a2d35);
}
.pol-btn--ghost:hover {
  background: var(--bg-card-secondary, #22232b);
  color: var(--text-primary);
}

.pol-alert {
  font-size: 0.82rem;
  border-radius: 8px;
}

/* vue-select dropdown appended to body — z-index must beat modal backdrop (1060) */
body > div[style*="position"] > .vs__dropdown-menu {
  z-index: 99999 !important;
  background: var(--bg-card, #1e1f26) !important;
  border: 1px solid var(--border-separator, #2a2d35) !important;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  padding: 4px;
}
body > div[style*="position"] > .vs__dropdown-menu .vs__dropdown-option {
  color: var(--text-primary, #e4e6eb);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
}
body > div[style*="position"] > .vs__dropdown-menu .vs__dropdown-option--highlight {
  background: rgba(99, 102, 241, 0.15);
  color: #818cf8;
}
body > div[style*="position"] > .vs__dropdown-menu .vs__no-options {
  color: var(--text-secondary);
  font-size: 0.82rem;
}
</style>

<script>
import StartTrialFooter from '@/views/integrations/StartTrialFooter';
import InboxSidebar from '@/components/integrations/protokol/inbox/InboxSidebar';
import InboxEmailList from '@/components/integrations/protokol/inbox/InboxEmailList';
import InboxEmailDetail from '@/components/integrations/protokol/inbox/InboxEmailDetail';
import InboxComposePanel from '@/components/integrations/protokol/inbox/InboxComposePanel';
import InboxModals from '@/components/integrations/protokol/inbox/InboxModals';
import VSelect from 'vue-select';

export default {
  name: 'protokol-inbox',
  components: { 
    StartTrialFooter,
    InboxSidebar,
    InboxEmailList,
    InboxEmailDetail,
    InboxComposePanel,
    InboxModals,
    VSelect
  },

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutsideHandler = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutsideHandler);
      },
      unbind(el) { document.removeEventListener('click', el._clickOutsideHandler); },
    },
  },

  props: {
    model:   { type: Object,  default: () => null },
    active:  { type: Boolean, default: false },
    context: { type: Object,  default: () => null },
  },

  data() {
    return {
      showAccounts: false,
      showCompose:  false,
      composeMode:  'dialog',
      composeTitle: '',
      emailPage:    1,
      pageSize:     25,

      accounts:        [],
      loadingAccounts: false,
      accountError:    null,
      selectedAccount: null,

      addingAccount:    false,
      addAccountError:  null,
      newAccount:       this.emptyAccount(),
      simplePassword:   '',
      showAdvanced:     false,
      detectedProvider: null,
      manualProvider:   null,
      skipConnectionTest: false,

      sending:        false,
      inlineSending:   false,
      sendError: null,
      emailForm: { to: '', cc: '', subject: '', body: '', htmlBody: '', inReplyTo: '', references: [] },
      showToSuggestions: false,
      showCc: false,
      allContacts: new Map(),

      emails:       [],
      threads:      [],
      loadingEmails: false,
      emailsError:   null,
      inboxFolder:   'INBOX',
      totalEmails:   0,
      totalPages:    1,

      imapFolders:   [],
      newFolderName: '',
      folderError:   null,

      selectedEmail:  null,
      threadMessages: [],
      loadingThread:  false,
      selectedEmails: new Set(),

      showMoreMenu: false,
      showMoveMenu: false,
      composeAttachments: [],

      // Policies
      showPolicies: false,
      policiesAccount: null,
      policies: [],
      loadingPolicies: false,
      policyError: null,
      showPolicyForm: false,
      editingPolicy: null,
      policyForm: { type: 'data_access', config: { targets: [], actions: [] } },
      policyTargetEverybody: false,
      savingPolicy: false,
    };
  },

  computed: {
    folders() {
      return [
        { key: 'INBOX',   label: this.$t('inbox.inbox_folder'),   icon: 'inbox' },
        { key: 'Starred', label: this.$t('inbox.starred'), icon: 'star' },
        { key: 'Sent',    label: this.$t('inbox.sent_folder'),    icon: 'cursor' },
        { key: 'Drafts',  label: this.$t('inbox.drafts'),  icon: 'file-earmark-text' },
        { key: 'Spam',    label: this.$t('inbox.spam'),    icon: 'exclamation-octagon' },
        { key: 'Trash',   label: this.$t('inbox.trash'),   icon: 'trash' },
      ];
    },

    unreadCount() {
      return this.inboxFolder !== 'INBOX' ? 0 : (this.emails || []).filter(e => !e.is_read).length;
    },

    hasDraft() {
      return !!(this.emailForm.to || this.emailForm.subject || this.emailForm.htmlBody || this.composeAttachments.length);
    },

    allSelected() {
      return this.pagedEmails.length > 0 && this.pagedEmails.every(e => this.selectedEmails.has(e.id));
    },

    someSelected() {
      return this.pagedEmails.some(e => this.selectedEmails.has(e.id));
    },

    effectiveModel() {
      return this.context || this.model;
    },

    effectiveActive() {
      if (this.context) return true;
      return this.active;
    },

    projectUUID() {
      const p = this.$store.getters.getCurrentProject;
      return (p && p.uuid) ? p.uuid : 'test-project-uuid';
    },

    sortedEmails() {
      return this.pagedEmails;
    },

    knownContacts() {
      return Array.from(this.allContacts.entries()).map(([address, name]) => ({ address, name }));
    },

    pagedEmails() {
      if (this.threads && this.threads.length) {
        // Show one entry per thread (the latest message), sorted newest-first
        const latestPerThread = this.threads.map(t => t.latest || (t.messages && t.messages.length ? t.messages[t.messages.length - 1] : null)).filter(Boolean);
        return latestPerThread.sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
      }
      return [...this.emails].sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
    },

    composePlaceholder() {
      if (this.composeTitle === this.$t('inbox.reply')) return this.$t('inbox.writeReply');
      if (this.composeTitle === this.$t('inbox.forward')) return this.$t('inbox.writeForward');
      return this.$t('inbox.writeMessage');
    },

    moveTargetFolders() {
      const current = this.inboxFolder;
      return [
        { key: 'INBOX',   label: this.$t('inbox.inbox_folder'),   icon: 'inbox' },
        { key: 'Starred', label: this.$t('inbox.starred'), icon: 'star' },
        { key: 'Sent',    label: this.$t('inbox.sent_folder'),    icon: 'cursor' },
        { key: 'Drafts',  label: this.$t('inbox.drafts'),  icon: 'file-earmark-text' },
        { key: 'Spam',    label: this.$t('inbox.spam'),    icon: 'exclamation-octagon' },
        { key: 'Trash',   label: this.$t('inbox.trash'),   icon: 'trash' },
      ].filter(f => f.key !== current);
    },

    emailHtmlDoc() {
      if (!this.selectedEmail) return '';
      const html  = this.selectedEmail.html_body;
      const plain = this.selectedEmail.body;

      const hasOwnBackground = (h) => {
        if (/<(?:body|html|table)[^>]*\sbgcolor\s*=/i.test(h)) return true;
        if (/<(?:body|html)[^>]*\sstyle\s*=\s*"[^"]*background/i.test(h)) return true;
        const styleBlocks = h.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
        if (styleBlocks) {
          for (const block of styleBlocks) {
            const css = block.replace(/<\/?style[^>]*>/gi, '');
            if (/(?:^|[},\s])(?:body|html)\s*\{[^}]*background/im.test(css)) return true;
          }
        }
        return false;
      };

      const minimalBase = `<style>
        *{box-sizing:border-box}
        html,body{margin:0;padding:0}
        body{padding:16px 20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6}
        img{max-width:100%;height:auto}
        table{border-collapse:collapse;max-width:100%}
      </style>`;

      const darkBase = `<style>
        *{box-sizing:border-box}
        html,body{margin:0;padding:0;background:#2a2d35;color:#d1d5db}
        body{padding:20px 24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6}
        img{max-width:100%;height:auto}
        table{border-collapse:collapse;max-width:100%}
        a{color:#818cf8;text-decoration:none}
        a:hover{text-decoration:underline;color:#a5b4fc}
        strong{font-weight:600;color:#f3f4f6}
        hr{border:none;border-top:1px solid #3f4350;margin:1em 0}
      </style>`;

      const plainBase = `<style>
        html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.75;color:#d1d5db;background:#2a2d35}
        body{padding:20px 24px}
        pre{margin:0;font-family:inherit;font-size:14px;white-space:pre-wrap;word-break:break-word;line-height:1.75}
        a{color:#818cf8;text-decoration:none}
        a:hover{text-decoration:underline;color:#a5b4fc}
        strong{color:#f3f4f6;font-weight:600}
        hr{border:none;border-top:1px solid #3f4350;margin:1em 0}
      </style>`;

      if (html) {
        const styled = hasOwnBackground(html);
        const base = styled ? minimalBase : darkBase;
        if (/^<!doctype|^<html/i.test(html.trim())) {
          if (/<head([^>]*)>/i.test(html)) {
            return html.replace(/<head([^>]*)>/i, `<head$1>${base}`);
          }
          return html.replace(/<html([^>]*)>/i, `<html$1><head>${base}</head>`);
        }
        return `<!DOCTYPE html><html><head><meta charset="utf-8">${base}</head><body>${html}</body></html>`;
      }
      const esc = plain
        .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      const formatted = esc
        .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
        .replace(/^---+\s*$/gm, '<hr>')
        .replace(/(https?:\/\/[^\s)<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
      return `<!DOCTYPE html><html><head><meta charset="utf-8">${plainBase}</head><body><pre style="margin:0;font-family:inherit;font-size:14px;white-space:pre-wrap;word-break:break-word;line-height:1.65">${formatted}</pre></body></html>`;
    },

    filteredContacts() {
      const q = (this.emailForm.to || '').toLowerCase();
      if (!q) return this.knownContacts.slice(0, 8);
      return this.knownContacts.filter(c =>
        c.address.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
      ).slice(0, 8);
    },

    providerOptions() {
      return [{ value: null, text: '— select provider —' }].concat(
        this.providerPresets().map(p => ({ value: p.name, text: p.name }))
      );
    },

    activeProvider() {
      if (this.detectedProvider) return this.detectedProvider;
      if (this.manualProvider) return this.providerPresets().find(p => p.name === this.manualProvider) || null;
      return null;
    },

    availableRoles() {
      const roles = this.$store.getters.getRoles || [];
      return roles.map(role => ({ uuid: role.uuid, name: role.name }));
    },

    canSend() {
      if (!this.selectedAccount) return false;
      const actions = this.selectedAccount.allowed_actions || [];
      return actions.includes('send');
    },

    canDelete() {
      if (!this.selectedAccount) return false;
      const actions = this.selectedAccount.allowed_actions || [];
      return actions.includes('delete');
    },

    canRead() {
      if (!this.selectedAccount) return false;
      const actions = this.selectedAccount.allowed_actions || [];
      return actions.includes('read');
    },

    isOwner() {
      return this.selectedAccount && this.selectedAccount.is_owner === true;
    },
  },

  watch: {
    effectiveActive(val) { if (val) this.loadAccounts(); },
    'newAccount.email'(email) { this.onSimpleEmailInput(email); },
    selectedAccount() { this.emailPage = 1; this.selectedEmails = new Set(); this.loadEmails(); this.loadFolders(); },
  },

  created() {
    if (this.effectiveActive) this.loadAccounts();
    this._refreshInterval = setInterval(() => {
      if (this.selectedAccount && !this.loadingEmails && !this.selectedEmail) {
        this.loadEmails({ silent: true });
      }
    }, 60 * 1000);
  },

  mounted() {
    document.body.classList.add('inbox-page-active');
  },

  beforeDestroy() {
    document.body.classList.remove('inbox-page-active');
    if (this._markReadTimer) clearTimeout(this._markReadTimer);
    if (this._refreshInterval) clearInterval(this._refreshInterval);
  },

  methods: {
    emptyAccount() {
      return {
        name: '', email: '',
        imap_config: { host: '', port: 993, username: '', password: '', use_tls: true, protocol: 'imap' },
        smtp_config: { host: '', port: 587, username: '', password: '', use_tls: true },
      };
    },

    async loadAccounts() {
      this.loadingAccounts = true;
      this.accountError = null;
      try {
        const res = await this.$integrationApi.get('/inbox/accounts');
        this.accounts = res.data || [];
        if (this.accounts.length && !this.selectedAccount) this.selectedAccount = this.accounts[0];
      } catch (err) {
        this.accountError = (err.response && err.response.data && err.response.data.message) || err.message;
      } finally {
        this.loadingAccounts = false;
      }
    },

    selectAccount(acc) {
      if (!this.selectedAccount || this.selectedAccount.id !== acc.id) {
        this.allContacts = new Map();
      }
      this.selectedAccount = acc;
    },

    switchFolder(folder) {
      if (folder === 'Drafts' && this.hasDraft && !this.showCompose) {
        this.showCompose = true;
        this.composeMode = 'dialog';
        return;
      }
      this.inboxFolder = folder;
      this.emailPage = 1;
      this.selectedEmails = new Set();
      this.selectedEmail = null;
      this.loadEmails();
    },

    prevPage() {
      if (this.emailPage > 1) { this.emailPage--; this.selectedEmails = new Set(); this.loadEmails(); }
    },

    nextPage() {
      if (this.emailPage < this.totalPages) { this.emailPage++; this.selectedEmails = new Set(); this.loadEmails(); }
    },

    onPageSizeChange(val) {
      this.pageSize = parseInt(val, 10) || 25;
      this.emailPage = 1;
      this.selectedEmails = new Set();
      this.loadEmails();
    },

    openAccountOptions(acc) {
      this.showAccounts = true;
    },

    async loadFolders() {
      if (!this.selectedAccount) return;
      try {
        const res = await this.$integrationApi.get(`/inbox/accounts/${this.selectedAccount.id}/folders`);
        this.imapFolders = (res.data || []).filter(f =>
          !['INBOX','Sent','Drafts','Spam','Trash','Starred','[Gmail]/Sent Mail','[Gmail]/Drafts','[Gmail]/Trash','[Gmail]/Spam','[Gmail]/Starred'].includes(f.name)
        );
      } catch (e) { /* silent */ }
    },

    openCreateFolderModal() {
      this.newFolderName = '';
      this.folderError = null;
      this.$mainDialog.show('inbox-create-folder-modal');
    },

    submitCreateFolderEnter() {
      if (this.newFolderName.trim()) this.submitCreateFolder();
    },

    async submitCreateFolder(bvModalEvt) {
      if (bvModalEvt && bvModalEvt.preventDefault) bvModalEvt.preventDefault();
      if (!this.newFolderName.trim()) return;
      this.folderError = null;
      try {
        await this.$integrationApi.post(`/inbox/accounts/${this.selectedAccount.id}/folders`, { name: this.newFolderName.trim() });
        this.$mainDialog.hide('inbox-create-folder-modal');
        await this.loadFolders();
        this.$bvToast.toast(`Folder "${this.newFolderName.trim()}" created!`, { title: 'Folder', variant: 'success', solid: true, autoHideDelay: 3000 });
      } catch (err) {
        this.folderError = (err.response && err.response.data && err.response.data.message) || err.message;
      }
    },

    providerPresets() {
      return [
        { name: 'Gmail', domains: ['gmail.com','googlemail.com'], helpUrl: 'https://support.google.com/accounts/answer/185833', imap: { host: 'imap.gmail.com', port: 993, use_tls: true }, smtp: { host: 'smtp.gmail.com', port: 587, use_tls: true } },
        { name: 'Google Workspace', domains: [], helpUrl: 'https://support.google.com/accounts/answer/185833', imap: { host: 'imap.gmail.com', port: 993, use_tls: true }, smtp: { host: 'smtp.gmail.com', port: 587, use_tls: true } },
        { name: 'Outlook / Hotmail', domains: ['outlook.com','hotmail.com','live.com','msn.com'], helpUrl: 'https://support.microsoft.com/en-us/account-billing/using-app-passwords-with-apps-that-don-t-support-two-step-verification-5896ed9b-4263-e681-128a-a6f2979a7944', imap: { host: 'outlook.office365.com', port: 993, use_tls: true }, smtp: { host: 'smtp.office365.com', port: 587, use_tls: true } },
        { name: 'Yahoo Mail', domains: ['yahoo.com','yahoo.co.uk','yahoo.de','yahoo.fr'], helpUrl: 'https://help.yahoo.com/kb/generate-third-party-passwords-sln15241.html', imap: { host: 'imap.mail.yahoo.com', port: 993, use_tls: true }, smtp: { host: 'smtp.mail.yahoo.com', port: 587, use_tls: true } },
        { name: 'iCloud Mail', domains: ['icloud.com','me.com','mac.com'], helpUrl: 'https://support.apple.com/en-us/102654', imap: { host: 'imap.mail.me.com', port: 993, use_tls: true }, smtp: { host: 'smtp.mail.me.com', port: 587, use_tls: true } },
      ];
    },

    onSimpleEmailInput(email) {
      const domain = (email || '').split('@')[1] || '';
      const preset = this.providerPresets().find(p => p.domains.includes(domain.toLowerCase()));
      this.detectedProvider = preset || null;
      if (preset) {
        Object.assign(this.newAccount.imap_config, preset.imap);
        Object.assign(this.newAccount.smtp_config, preset.smtp);
      }
      this.newAccount.imap_config.username = email;
      this.newAccount.smtp_config.username = email;
      if (!this.newAccount.name) this.newAccount.name = email;
    },

    openAddAccountModal() {
      this.newAccount = this.emptyAccount();
      this.simplePassword = '';
      this.showAdvanced = false;
      this.detectedProvider = null;
      this.manualProvider = null;
      this.skipConnectionTest = false;
      this.addAccountError = null;
      this.$mainDialog.show('inbox-add-account-modal');
    },

    resolveHostsFromEmail() {
      const provider = this.activeProvider;
      if (provider) return { imap: { ...provider.imap }, smtp: { ...provider.smtp } };
      return { imap: { host: '', port: 993, use_tls: true }, smtp: { host: '', port: 587, use_tls: true } };
    },

    async submitAddAccount(bvModalEvt) {
      if (bvModalEvt && bvModalEvt.preventDefault) bvModalEvt.preventDefault();
      this.addingAccount = true;
      this.addAccountError = null;
      try {
        const password = this.simplePassword || this.newAccount.imap_config.password;
        const hosts    = this.resolveHostsFromEmail();
        if (!hosts.imap.host && !(this.showAdvanced && this.newAccount.imap_config.host)) {
          this.addAccountError = 'Unknown email provider. Select your provider or use Advanced settings.';
          return;
        }
        const payload = {
          name: this.newAccount.name || this.newAccount.email,
          email: this.newAccount.email,
          is_confirmed: this.skipConnectionTest,
          imap_config: {
            ...hosts.imap,
            username: this.newAccount.imap_config.username || this.newAccount.email,
            password,
            protocol: 'imap',
            ...(this.showAdvanced && this.newAccount.imap_config.host ? {
              host: this.newAccount.imap_config.host, port: this.newAccount.imap_config.port,
              use_tls: this.newAccount.imap_config.use_tls, username: this.newAccount.imap_config.username,
              password: this.newAccount.imap_config.password || password,
            } : {}),
          },
          smtp_config: {
            ...hosts.smtp,
            username: this.newAccount.smtp_config.username || this.newAccount.email,
            password,
            ...(this.showAdvanced && this.newAccount.smtp_config.host ? {
              host: this.newAccount.smtp_config.host, port: this.newAccount.smtp_config.port,
              use_tls: this.newAccount.smtp_config.use_tls, username: this.newAccount.smtp_config.username,
              password: this.newAccount.smtp_config.password || password,
            } : {}),
          },
        };
        await this.$integrationApi.post('/inbox/accounts', payload);
        await this.loadAccounts();
        this.$mainDialog.hide('inbox-add-account-modal');
      } catch (err) {
        this.addAccountError = (err.response && err.response.data && err.response.data.message) || err.message;
      } finally {
        this.addingAccount = false;
      }
    },

    async testConnection(acc) {
      try {
        const res = await this.$integrationApi.post('/inbox/accounts/test', {
          email: acc.email, imap_config: acc.imap_config, smtp_config: acc.smtp_config,
        });
        const variant = res.data.success ? 'success' : 'danger';
        const msg     = res.data.success ? 'Connection successful!' : 'Connection failed.';
        this.$bvToast.toast(msg, { title: 'Test', variant, solid: true });
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Connection Failed', variant: 'danger', solid: true });
      }
    },

    async deleteAccount(acc) {
      const ok = await this.$mainDialog.msgBoxConfirm(`Delete account "${acc.name}" (${acc.email})?`, { title: 'Confirm Delete', okVariant: 'danger', okTitle: 'Delete' });
      if (!ok) return;
      try {
        await this.$integrationApi.delete(`/inbox/accounts/${acc.id}`);
        if (this.selectedAccount && this.selectedAccount.id === acc.id) this.selectedAccount = null;
        await this.loadAccounts();
      } catch (err) {
        this.accountError = (err.response && err.response.data && err.response.data.message) || err.message;
      }
    },

    async renameAccount({ account, name }) {
      try {
        await this.$integrationApi.put(`/inbox/accounts/${account.id}`, { name });
        await this.loadAccounts();
        if (this.selectedAccount && this.selectedAccount.id === account.id) {
          this.selectedAccount = this.accounts.find(a => a.id === account.id) || this.selectedAccount;
        }
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      }
    },

    // ── Policies ──
    async openPolicies(acc) {
      this.policiesAccount = acc;
      this.showPolicies = true;
      this.showPolicyForm = false;
      this.editingPolicy = null;
      this.policyTargetEverybody = false;
      this.policyError = null;
      this.$store.dispatch('fetchRoles').catch(() => {});
      await this.loadPolicies();
    },

    async loadPolicies() {
      if (!this.policiesAccount) return;
      this.loadingPolicies = true;
      try {
        const res = await this.$integrationApi.get(`/inbox/accounts/${this.policiesAccount.id}/policies`);
        this.policies = res.data || [];
      } catch (err) {
        this.policyError = (err.response && err.response.data && err.response.data.message) || err.message;
      } finally {
        this.loadingPolicies = false;
      }
    },

    editPolicy(policy) {
      this.editingPolicy = policy;
      const targets = policy.config.targets || [];
      const isEverybody = targets.includes('everybody');
      this.policyTargetEverybody = isEverybody;
      this.policyForm = {
        type: policy.type || 'data_access',
        config: {
          targets: isEverybody ? ['everybody'] : [...targets],
          actions: [...(policy.config.actions || [])],
        },
      };
      this.showPolicyForm = true;
    },

    onEverybodyToggle(checked) {
      this.policyTargetEverybody = checked;
      if (checked) {
        this.policyForm.config.targets = ['everybody'];
      } else {
        this.policyForm.config.targets = [];
      }
    },

    toggleAction(action) {
      const idx = this.policyForm.config.actions.indexOf(action);
      if (idx >= 0) {
        this.policyForm.config.actions.splice(idx, 1);
      } else {
        this.policyForm.config.actions.push(action);
      }
    },

    resolveTargetNames(targets) {
      if (!targets || !targets.length) return '—';
      const roles = this.$store.getters.getRoles || [];
      return targets.map(uuid => {
        const role = roles.find(r => r.uuid === uuid);
        return role ? role.name : uuid;
      }).join(', ');
    },

    async submitPolicy() {
      this.savingPolicy = true;
      this.policyError = null;
      try {
        const payload = { type: this.policyForm.type, config: this.policyForm.config };
        if (this.editingPolicy) {
          await this.$integrationApi.put(`/inbox/accounts/${this.policiesAccount.id}/policies/${this.editingPolicy.id}`, payload);
        } else {
          await this.$integrationApi.post(`/inbox/accounts/${this.policiesAccount.id}/policies`, payload);
        }
        this.showPolicyForm = false;
        this.editingPolicy = null;
        await this.loadPolicies();
      } catch (err) {
        this.policyError = (err.response && err.response.data && err.response.data.message) || err.message;
      } finally {
        this.savingPolicy = false;
      }
    },

    async removePolicy(policy) {
      const ok = await this.$mainDialog.msgBoxConfirm(this.$t('inbox.confirmDeletePolicy'), {
        title: this.$t('inbox.deletePolicy'), okVariant: 'danger', okTitle: this.$t('inbox.delete'),
      });
      if (!ok) return;
      try {
        await this.$integrationApi.delete(`/inbox/accounts/${this.policiesAccount.id}/policies/${policy.id}`);
        await this.loadPolicies();
      } catch (err) {
        this.policyError = (err.response && err.response.data && err.response.data.message) || err.message;
      }
    },

    openCompose(prefill = {}) {
      const hasPrefill = !!(prefill.to || prefill.subject || prefill.bodyHtml || prefill.title);
      if (hasPrefill) {
        this.composeTitle = prefill.title || this.$t('inbox.newMessage');
        this.emailForm    = {
          to: prefill.to || '', cc: '', subject: prefill.subject || '',
          body: '', htmlBody: prefill.bodyHtml || '',
          inReplyTo: prefill.inReplyTo || '', references: prefill.references || [],
        };
        this.showCc       = false;
        this.sendError    = null;
      } else {
        const panel = this.$refs.composePanel;
        const hasDraft = !!(this.emailForm.to || this.emailForm.subject || this.emailForm.htmlBody ||
          (panel && panel.getBodyHtml && panel.getBodyHtml().trim()));
        if (!hasDraft) {
          this.composeTitle = this.$t('inbox.newMessage');
          this.emailForm    = { to: '', cc: '', subject: '', body: '', htmlBody: '', inReplyTo: '', references: [] };
          this.showCc       = false;
          this.sendError    = null;
        }
      }
      this.showCompose  = true;
      this.composeMode  = 'dialog';
    },

    discardCompose() {
      this.showCompose  = false;
      this.emailForm    = { to: '', cc: '', subject: '', body: '', htmlBody: '', inReplyTo: '', references: [] };
      this.composeTitle = this.$t('inbox.newMessage');
      this.showCc       = false;
      this.sendError    = null;
      this.composeAttachments = [];
      const panel = this.$refs.composePanel;
      if (panel && panel.setBodyHtml) panel.setBodyHtml('');
    },

    async sendEmail() {
      this.sending = true;
      this.sendError = null;
      try {
        const panel = this.$refs.composePanel;
        const htmlBody = panel && panel.getBodyHtml ? panel.getBodyHtml() : this.emailForm.htmlBody;
        const payload  = {
          to: [{ address: this.emailForm.to }],
          subject: this.emailForm.subject,
          html_body: htmlBody,
          body: htmlBody.replace(/<[^>]+>/g, ''),
        };
        if (this.emailForm.cc) payload.cc = [{ address: this.emailForm.cc }];
        if (this.emailForm.inReplyTo) payload.in_reply_to = this.emailForm.inReplyTo;
        if (this.emailForm.references && this.emailForm.references.length) payload.references = this.emailForm.references;

        if (this.composeAttachments.length) {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(payload));
          for (const f of this.composeAttachments) fd.append('attachments', f);
          await this.$integrationApi.post(`/inbox/accounts/${this.selectedAccount.id}/send`, fd);
        } else {
          await this.$integrationApi.post(`/inbox/accounts/${this.selectedAccount.id}/send`, payload);
        }
        this.discardCompose();
        this.$bvToast.toast(this.$t('inbox.emailSent'), { title: this.$t('inbox.sent'), variant: 'success', solid: true, autoHideDelay: 3000 });
      } catch (err) {
        this.sendError = (err.response && err.response.data && err.response.data.message) || err.message;
      } finally {
        this.sending = false;
      }
    },

    execCmd(cmd, value = null) {
      const panel = this.$refs.composePanel;
      if (panel && panel.focusBody) panel.focusBody();
      document.execCommand(cmd, false, value);
    },

    insertLink() {
      const url = window.prompt('Enter URL:', 'https://');
      if (url) this.execCmd('createLink', url);
    },

    async handleInlineReplySend(data) {
      this.inlineSending = true;
      try {
        const payload = {
          to: [{ address: data.to }],
          subject: data.subject,
          html_body: data.htmlBody,
          body: data.htmlBody.replace(/<[^>]+>/g, ''),
        };
        if (data.cc) payload.cc = [{ address: data.cc }];
        if (data.inReplyTo) payload.in_reply_to = data.inReplyTo;
        if (data.references && data.references.length) payload.references = data.references;

        if (data.attachments && data.attachments.length) {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(payload));
          for (const f of data.attachments) fd.append('attachments', f);
          await this.$integrationApi.post(`/inbox/accounts/${this.selectedAccount.id}/send`, fd);
        } else {
          await this.$integrationApi.post(`/inbox/accounts/${this.selectedAccount.id}/send`, payload);
        }
        this.$bvToast.toast(this.$t('inbox.emailSent'), { title: this.$t('inbox.sent'), variant: 'success', solid: true, autoHideDelay: 3000 });
        this.loadEmails();
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.inlineSending = false;
      }
    },

    handleReplyPopout(prefill) {
      this.openCompose(prefill);
    },

    onComposeToInput() { this.showToSuggestions = true; },
    hideToSuggestions() { setTimeout(() => { this.showToSuggestions = false; }, 150); },
    selectContact(c) { this.emailForm.to = c.address; this.showToSuggestions = false; },

    replyTo(email) {
      const senderName = (email.from && email.from.name) || (email.from && email.from.address) || '';
      const senderAddr = (email.from && email.from.address) || '';
      const dateStr = email.received_at ? new Date(email.received_at).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
      const quoted = email.html_body || (email.body || '').replace(/\n/g, '<br>');
      // Build References chain: existing references + original message-id
      const refs = [...(email.references || [])];
      if (email.message_id && !refs.includes(email.message_id)) refs.push(email.message_id);
      this.openCompose({
        title:       this.$t('inbox.reply'),
        to:          senderAddr,
        subject:     email.subject ? (email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`) : '',
        inReplyTo:   email.message_id || '',
        references:  refs,
        bodyHtml:    `<div class="compose-reply-area"><br></div><div class="compose-quoted" contenteditable="false" style="margin-top:1rem"><div style="color:#6b7280;font-size:0.8125rem;margin-bottom:0.5rem">On ${dateStr}, ${senderName} &lt;${senderAddr}&gt; wrote:</div><blockquote style="border-left:2px solid #6c80ff;margin:0 0 0 0.5rem;padding-left:0.75rem;color:#9ca3af">${quoted}</blockquote></div>`,
      });
    },

    forwardEmail(email) {
      const dateStr = email.received_at ? new Date(email.received_at).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
      const fromStr = email.from ? `${email.from.name || ''} &lt;${email.from.address}&gt;` : '';
      const toStr = (email.to || []).map(t => `${t.name || ''} &lt;${t.address}&gt;`).join(', ');
      const ccStr = (email.cc || []).length ? `Cc: ${email.cc.map(c => `${c.name || ''} &lt;${c.address}&gt;`).join(', ')}<br>` : '';
      const quoted = email.html_body || (email.body || '').replace(/\n/g, '<br>');
      this.openCompose({
        title:    this.$t('inbox.forward'),
        subject:  email.subject ? (email.subject.startsWith('Fwd:') ? email.subject : `Fwd: ${email.subject}`) : '',
        bodyHtml: `<div class="compose-reply-area"><br></div><div class="compose-quoted" contenteditable="false" style="margin-top:1rem"><div style="color:#6b7280;font-size:0.8125rem">---------- Forwarded message ----------<br>From: ${fromStr}<br>Date: ${dateStr}<br>Subject: ${email.subject || '(no subject)'}<br>To: ${toStr}<br>${ccStr}</div><br>${quoted}</div>`,
      });
    },

    imapFolderName(folder) {
      const host    = this.selectedAccount && this.selectedAccount.imap_config && this.selectedAccount.imap_config.host;
      const isGmail = host && host.includes('gmail.com');
      if (isGmail) {
        const map = { Sent: '[Gmail]/Sent Mail', Drafts: '[Gmail]/Drafts', Trash: '[Gmail]/Trash', Spam: '[Gmail]/Spam', Starred: '[Gmail]/Starred' };
        return map[folder] || folder;
      }
      return folder;
    },

    async loadEmails({ silent = false } = {}) {
      if (!this.selectedAccount) return;
      if (!silent) {
        this.loadingEmails = true;
        this.emailsError   = null;
        this.emails        = [];
        this.threads       = [];
      }
      try {
        const params = { folder: this.imapFolderName(this.inboxFolder), limit: this.pageSize, page: this.emailPage, threaded: 'true' };
        if (silent && this._lastTotal) {
          params.sinceTotal = this._lastTotal;
        }
        const res = await this.$integrationApi.get(`/inbox/accounts/${this.selectedAccount.id}/fetch`, { params });
        const data = res.data || {};
        if (data.unchanged) return;
        this.emails     = Array.isArray(data.emails) ? data.emails : (Array.isArray(data) ? data : []);
        this.threads    = Array.isArray(data.threads) ? data.threads : [];
        this.totalEmails = data.total || this.emails.length;
        this.totalPages  = data.pages  || Math.max(1, Math.ceil(this.totalEmails / this.pageSize));
        this._lastTotal  = data.total || 0;
        this._threadCache = {};
        const contacts = new Map(this.allContacts);
        const add = (a) => { if (a && a.address && !contacts.has(a.address)) contacts.set(a.address, a.name || a.address); };
        this.emails.forEach(e => {
          if (e.from) add(e.from);
          (e.to || []).forEach(add);
          (e.cc || []).forEach(add);
        });
        this.allContacts = contacts;
      } catch (err) {
        if (!silent) {
          this.emailsError = (err.response && err.response.data && err.response.data.message) || err.message;
        }
      } finally {
        if (!silent) this.loadingEmails = false;
      }
    },

    async openEmail(email) {
      this.selectedEmail = email;
      // Find thread from the list response
      const thread = this.threads.find(t => t.messages.some(m => m.id === email.id));
      this.threadMessages = thread && thread.count > 1 ? thread.messages : [];

      if (this._markReadTimer) clearTimeout(this._markReadTimer);
      if (!email.is_read) {
        this._markReadTimer = setTimeout(() => {
          if (this.selectedEmail && this.selectedEmail.id === email.id) {
            this.markRead(email, true);
          }
        }, 1500);
      }

      // Build cache key and check for cached content
      if (!this._threadCache) this._threadCache = {};
      const folder = this.imapFolderName(this.inboxFolder);
      const uids = thread && thread.count > 1
        ? thread.messages.map(m => m.imap_uid).sort((a, b) => a - b).join(',')
        : String(email.imap_uid);
      const cacheKey = `${this.selectedAccount.id}:${folder}:${uids}`;

      if (this._threadCache[cacheKey]) {
        this._applyThreadData(email, thread, this._threadCache[cacheKey]);
        return;
      }

      // Fetch full content for the thread (or single email) via /thread endpoint
      this.loadingThread = true;
      try {
        const res = await this.$integrationApi.get(`/inbox/accounts/${this.selectedAccount.id}/thread`, {
          params: { folder, uids },
        });
        if (res.data && res.data.emails && this.selectedEmail && this.selectedEmail.id === email.id) {
          this._threadCache[cacheKey] = res.data.emails;
          this._applyThreadData(email, thread, res.data.emails);
        }
      } catch (err) {
        // Fall back to header-only data already set
      } finally {
        this.loadingThread = false;
      }
    },

    _applyThreadData(email, thread, fullEmails) {
      const fullCurrent = fullEmails.find(e => e.imap_uid === email.imap_uid);
      if (fullCurrent) {
        const merged = { ...fullCurrent, id: email.id, _fullFetched: true, is_read: email.is_read, is_flagged: email.is_flagged };
        this.selectedEmail = merged;
        const idx = this.emails.findIndex(e => e.id === email.id);
        if (idx !== -1) this.$set(this.emails, idx, merged);
      }
      if (thread && thread.count > 1) {
        this.threadMessages = fullEmails.map(fe => {
          const headerMsg = thread.messages.find(m => m.imap_uid === fe.imap_uid);
          return {
            ...fe,
            id: headerMsg ? headerMsg.id : fe.id,
            is_read: headerMsg ? headerMsg.is_read : fe.is_read,
            is_flagged: headerMsg ? headerMsg.is_flagged : fe.is_flagged,
            _fullFetched: true,
          };
        });
      }
    },

    closeDetail() {
      if (this._markReadTimer) clearTimeout(this._markReadTimer);
      this.selectedEmail = null;
      this.threadMessages = [];
    },

    resizeEmailFrame() {
      const frame = this.$refs.emailFrame;
      if (!frame) return;
      try {
        const h = frame.contentDocument.documentElement.scrollHeight;
        frame.style.height = Math.max(200, h) + 'px';
      } catch (e) {}
    },

    async markRead(email, isRead) {
      const action = isRead ? 'read' : 'unread';
      try {
        await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/${action}`, {
          uid: email.imap_uid, folder: this.imapFolderName(this.inboxFolder),
        });
        email.is_read = isRead;
      } catch (e) { /* silent */ }
    },

    toggleRead(email) { this.markRead(email, !email.is_read); },

    async toggleStar(email) {
      const flagged = !email.is_flagged;
      try {
        await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/${flagged ? 'flag' : 'unflag'}`, {
          uid: email.imap_uid, folder: this.imapFolderName(this.inboxFolder),
        });
        email.is_flagged = flagged;
      } catch (e) { /* silent */ }
    },

    async deleteEmail(email) {
      const currentFolder = this.imapFolderName(this.inboxFolder);
      const trashFolder   = this.imapFolderName('Trash');
      const inTrash       = currentFolder === trashFolder;
      const label         = inTrash ? 'Permanently delete this message?' : 'Move this message to Trash?';
      const ok = await this.$mainDialog.msgBoxConfirm(label, { title: 'Confirm Delete', okVariant: 'danger', okTitle: inTrash ? 'Delete' : 'Move to Trash', size: 'sm' });
      if (!ok) return;
      try {
        if (inTrash) {
          await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/delete`, {
            uid: email.imap_uid, folder: currentFolder,
          });
        } else {
          await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/${email.imap_uid}/move`, {
            target_folder: trashFolder, folder: currentFolder,
          });
        }
        this.emails = this.emails.filter(e => e.id !== email.id);
        this.closeDetail();
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      }
    },

    async bulkMarkRead(isRead) {
      if (!this.selectedEmails.size) return;
      const uids = this.emails
        .filter(e => this.selectedEmails.has(e.id))
        .map(e => e.imap_uid);
      try {
        await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/bulk-${isRead ? 'read' : 'unread'}`, {
          uids, folder: this.imapFolderName(this.inboxFolder),
        });
        this.emails.forEach(e => {
          if (this.selectedEmails.has(e.id)) e.is_read = isRead;
        });
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.selectedEmails = new Set();
      }
    },

    async bulkDelete() {
      if (!this.selectedEmails.size) return;
      const currentFolder = this.imapFolderName(this.inboxFolder);
      const trashFolder   = this.imapFolderName('Trash');
      const inTrash       = currentFolder === trashFolder;
      const ok = await this.$mainDialog.msgBoxConfirm(
        inTrash ? `Permanently delete ${this.selectedEmails.size} message(s)?` : `Move ${this.selectedEmails.size} message(s) to Trash?`,
        { title: 'Confirm Delete', okVariant: 'danger', okTitle: inTrash ? 'Delete' : 'Move to Trash', size: 'sm' }
      );
      if (!ok) return;
      const selected = this.emails.filter(e => this.selectedEmails.has(e.id));
      const uids = selected.map(e => e.imap_uid);
      try {
        if (inTrash) {
          await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/bulk-delete`, {
            uids, folder: currentFolder,
          });
        } else {
          await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/bulk-move`, {
            uids, folder: currentFolder, target_folder: trashFolder,
          });
        }
        this.emails = this.emails.filter(e => !this.selectedEmails.has(e.id));
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.selectedEmails = new Set();
      }
    },

    toggleSelectEmail(id) {
      const s = new Set(this.selectedEmails);
      s.has(id) ? s.delete(id) : s.add(id);
      this.selectedEmails = s;
    },

    toggleSelectAll() {
      this.selectedEmails = this.allSelected
        ? new Set()
        : new Set(this.pagedEmails.map(e => e.id));
    },

    closeMoreMenu() {
      this.showMoreMenu = false;
      this.showMoveMenu = false;
    },

    async bulkStar(flag) {
      this.showMoreMenu = false;
      if (!this.selectedEmails.size) return;
      const selected = this.emails.filter(e => this.selectedEmails.has(e.id));
      const uids = selected.map(e => e.imap_uid);
      try {
        await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/${flag ? 'bulk-flag' : 'bulk-unflag'}`, {
          uids, folder: this.imapFolderName(this.inboxFolder),
        });
        selected.forEach(e => { e.is_flagged = flag; });
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.selectedEmails = new Set();
      }
    },

    async bulkMoveTo(targetFolder) {
      this.showMoreMenu = false;
      this.showMoveMenu = false;
      if (!this.selectedEmails.size) return;
      const selected  = this.emails.filter(e => this.selectedEmails.has(e.id));
      const uids       = selected.map(e => e.imap_uid);
      const fromFolder = this.imapFolderName(this.inboxFolder);
      const toFolder   = this.imapFolderName(targetFolder);
      try {
        await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/bulk-move`, {
          uids, folder: fromFolder, target_folder: toFolder,
        });
        this.emails = this.emails.filter(e => !this.selectedEmails.has(e.id));
        this.$bvToast.toast(`Moved ${selected.length} message(s) to ${targetFolder}`, { title: 'Moved', variant: 'success', solid: true, autoHideDelay: 3000 });
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.selectedEmails = new Set();
      }
    },

    bulkMoveToSpam() {
      this.bulkMoveTo('Spam');
    },

    async moveEmailTo(email, targetFolder) {
      const fromFolder = this.imapFolderName(this.inboxFolder);
      const toFolder   = this.imapFolderName(targetFolder);
      try {
        await this.$integrationApi.put(`/inbox/accounts/${this.selectedAccount.id}/emails/${email.imap_uid}/move`, {
          target_folder: toFolder, folder: fromFolder,
        });
        this.emails = this.emails.filter(e => e.id !== email.id);
        this.closeDetail();
      } catch (err) {
        this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || err.message, { title: 'Error', variant: 'danger', solid: true });
      }
    },

    senderInitial(email) {
      const name = (email.from && (email.from.name || email.from.address)) || '?';
      return name.trim()[0] || '?';
    },

    emailPreview(email) {
      const src  = email.body || email.html_body || '';
      const text = src.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      return text.slice(0, 90);
    },

    attachmentDataUrl(att) {
      if (!att.data) return '';
      const b64 = typeof att.data === 'string' ? att.data : btoa(String.fromCharCode(...new Uint8Array(att.data)));
      return `data:${att.content_type || 'application/octet-stream'};base64,${b64}`;
    },

    async downloadAttachment(att) {
      // If attachment data is already inlined as base64, use it directly
      if (att.data) {
        const url = this.attachmentDataUrl(att);
        if (!url) return;
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename || 'attachment';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      // Otherwise fetch the binary from the API
      if (!this.selectedEmail || !this.selectedAccount) return;
      const partId = att.part_id || att.id || att.section || att.filename;
      if (!partId) return;
      try {
        const folder = this.imapFolderName(this.inboxFolder);
        const resp = await this.$integrationApi.get(
          `/inbox/accounts/${this.selectedAccount.id}/emails/${this.selectedEmail.imap_uid}/attachment/${encodeURIComponent(partId)}`,
          { responseType: 'blob', params: { folder } }
        );
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', att.filename || 'attachment');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.$bvToast.toast(
          (err.response && err.response.data && err.response.data.message) || err.message,
          { title: 'Download Error', variant: 'danger', solid: true }
        );
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
      return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
    },

    onFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      this.composeAttachments.push(...files);
      e.target.value = '';
    },

    formatDate(d, mode) {
      if (!d) return '';
      const date = new Date(d);
      if (mode === 'short') {
        const now = new Date();
        if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (date.getFullYear() === now.getFullYear()) return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
      }
      if (mode === 'time') return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
    },

    dismissError(type) {
      if (type === 'emails') this.emailsError = null;
      if (type === 'account') this.accountError = null;
    },

    execCmd(cmd, val = null) {
      document.execCommand(cmd, false, val);
      if (this.$refs.composePanel && this.$refs.composePanel.focusBody) {
        this.$refs.composePanel.focusBody();
      }
    },

    onBodyInput() {
      if (this.$refs.composePanel && this.$refs.composePanel.getBodyHtml) {
        this.emailForm.htmlBody = this.$refs.composePanel.getBodyHtml();
      }
    },

    resizeEmailFrame() {
      this.$nextTick(() => {
        const frame = this.$refs.emailFrame;
        if (frame && frame.contentDocument) {
          const body = frame.contentDocument.body;
          if (body) {
            frame.style.height = body.scrollHeight + 'px';
          }
        }
      });
    },

    closeMoreMenu() {
      this.showMoreMenu = false;
      this.showMoveMenu = false;
    },
  },
};
</script>
